import { useMutation } from '@tanstack/react-query';
import { isEqual } from 'lodash';

import { useQuestionExamGradeCtx } from '@/data/context/QuestionExamGradeContext';
import useEditableQuestion from '@/data/hook/question/useEditableQuestion';
import {
  createOrderingColumn,
  updateOrderingColumn,
} from '@/data/services/question/orderingColumnServices';
import {
  createOrderingQuestionIntoActivity,
  updateOrderingQuestionIntoActivity,
} from '@/data/services/question/orderingQuestionServices';
import { QuestionResolutionError } from '@/errors/questionResolution';
import Activity from '@/models/Activity';
import { FlowColumn } from '@/models/DNDOrdenation';
import { DNDOrdenationQuestion } from '@/models/Question';
import { adapterOrderingAnswers } from '@/utils/questions/adapterAnswersDTO';
import {
  adapterFeedbacks,
  sanitizeFeedbacks,
} from '@/utils/questions/adapterFeedbacks';
import { getQuestionErrorMessage } from '@/utils/questions/getQuestionErrorMessage';
import { sanitizeOrderingAnswers } from '@/utils/questions/sanitizeAnswers';
import { normalizeTranslatableField } from '@/utils/translatable-field/normalizeTranslatableField';
import alert from '@/utils/shared/alert';
import OrderingForm, { OrderingFormFields } from './OrderingForm';

type OrderingFormServiceProps = {
  activity: Activity;
  question: DNDOrdenationQuestion;
  isActive?: boolean;
};

export default function OrderingFormService({
  activity,
  question,
  isActive,
}: OrderingFormServiceProps) {
  const gradeCtx = useQuestionExamGradeCtx();
  const { ensureEditable, resolveEditable } = useEditableQuestion(activity);
  const column = question.columns?.at(0);

  const handleCreate = async (
    data: OrderingFormFields,
  ): Promise<DNDOrdenationQuestion> => {
    const { maxGrade, ...rest } = data;
    const editableActivity = await ensureEditable();
    const createdActivity = await createOrderingQuestionIntoActivity(
      editableActivity.id,
      {
        type: question.type,
        identifier: question.identifier,
        order: question.order,
        title: rest.title,
        context: rest.context,
        feedbacks: sanitizeFeedbacks(rest.feedbacks),
      },
    );

    const createdColumn = await createOrderingColumn({
      dndOrdenationActivity: createdActivity.id,
      name: 'default',
      flow: rest.flow,
      items: sanitizeOrderingAnswers(rest.orderingItems),
    });

    const saved = { ...createdActivity, columns: [createdColumn] };
    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({ question: saved, maxGrade });
    }
    return saved;
  };

  const handleUpdate = async (
    data: OrderingFormFields,
  ): Promise<DNDOrdenationQuestion> => {
    const { maxGrade, ...rest } = data;
    const oldValues: Omit<OrderingFormFields, 'maxGrade'> = {
      title: normalizeTranslatableField(question.title),
      context: normalizeTranslatableField(question.context),
      flow: (column?.flow as FlowColumn) ?? FlowColumn.VERTICAL,
      orderingItems: adapterOrderingAnswers(column?.items ?? []),
      feedbacks: adapterFeedbacks(question.feedbacks ?? []),
    };

    const activityChanges: Partial<
      Pick<OrderingFormFields, 'title' | 'context' | 'feedbacks'>
    > = {};
    if (!isEqual(oldValues.title, rest.title)) {
      activityChanges.title = rest.title;
    }
    if (!isEqual(oldValues.context, rest.context)) {
      activityChanges.context = rest.context;
    }
    const newSanitizedFeedbacks = sanitizeFeedbacks(rest.feedbacks);
    const oldSanitizedFeedbacks = sanitizeFeedbacks(oldValues.feedbacks);
    if (!isEqual(oldSanitizedFeedbacks, newSanitizedFeedbacks)) {
      activityChanges.feedbacks = newSanitizedFeedbacks;
    }

    const oldSanitizedOrderingItems = sanitizeOrderingAnswers(
      oldValues.orderingItems,
    );
    const newSanitizedOrderingItems = sanitizeOrderingAnswers(
      rest.orderingItems,
    );
    const columnChanges: {
      flow?: FlowColumn;
      items?: typeof newSanitizedOrderingItems;
    } = {};
    if (!isEqual(oldValues.flow, rest.flow)) {
      columnChanges.flow = rest.flow;
    }
    if (!isEqual(oldSanitizedOrderingItems, newSanitizedOrderingItems)) {
      columnChanges.items = newSanitizedOrderingItems;
    }

    const shouldUpdateColumn =
      Object.keys(columnChanges).length > 0 && !!column?.id;

    const { target, toClone, updateTarget } = await resolveEditable(question);
    const targetColumn = target.columns?.at(0);

    if (shouldUpdateColumn && !targetColumn?.id)
      throw new QuestionResolutionError();

    if (shouldUpdateColumn && columnChanges.items)
      columnChanges.items = toClone(
        columnChanges.items,
        q => q.columns?.at(0)?.items,
      );

    let saved = await updateTarget(
      activityChanges,
      updateOrderingQuestionIntoActivity,
    );

    if (shouldUpdateColumn && targetColumn?.id) {
      const updatedColumn = await updateOrderingColumn(
        targetColumn.id,
        columnChanges,
      );
      saved = { ...saved, columns: [updatedColumn] };
    }

    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({
        question: saved,
        originalQuestion: question,
        maxGrade,
      });
    }
    return saved;
  };

  const { mutateAsync: submitQuestion, isPending } = useMutation({
    mutationFn: (data: OrderingFormFields) =>
      !question.id ? handleCreate(data) : handleUpdate(data),
    onError: err => alert.error(getQuestionErrorMessage(err)),
  });

  return (
    <OrderingForm
      key={question.identifier}
      isActive={isActive}
      question={question}
      onSubmit={submitQuestion}
      isLoading={isPending}
    />
  );
}
